import { Injectable } from '@angular/core';
import { createStore, withProps } from '@ngneat/elf';
import {
  EMPTY_CHART_ID,
  EMPTY_MCONFIG_ID,
  EMPTY_QUERY_ID
} from '#common/constants/top';
import { ChartTypeEnum } from '#common/enums/chart/chart-type.enum';
import { MconfigParentTypeEnum } from '#common/enums/mconfig-parent-type.enum';
import { QueryStatusEnum } from '#common/enums/query-status.enum';
import { makeId } from '#common/functions/make-id';
import type { ChartX } from '#common/zod/backend/chart-x';
import type { MconfigX } from '#common/zod/backend/mconfig-x';
import type { TileX } from '#common/zod/backend/tile-x';
import type { Query } from '#common/zod/blockml/query';
import { BaseQuery } from './base.query';

export type ChartState = ChartX;

export const emptyMconfig: MconfigX = {
  structId: undefined,
  mconfigId: EMPTY_MCONFIG_ID,
  queryId: undefined,
  modelId: undefined,
  modelType: undefined,
  parentType: MconfigParentTypeEnum.Chart,
  parentId: undefined,
  dateRangeIncludesRightSide: undefined,
  storePart: undefined,
  modelLabel: undefined,
  modelFilePath: undefined,
  malloyQueryStable: undefined,
  malloyQueryExtra: undefined,
  compiledQuery: undefined,
  select: [],
  sortings: [],
  sorts: undefined,
  timezone: undefined,
  limit: undefined,
  filters: [],
  extendedFilters: [],
  fields: [],
  chart: undefined,
  serverTs: 1
};

export const emptyQuery: Query = {
  projectId: undefined,
  envId: undefined,
  connectionId: undefined,
  connectionType: undefined,
  queryId: EMPTY_QUERY_ID,
  reportId: undefined,
  reportStructId: undefined,
  sql: undefined,
  apiMethod: undefined,
  apiUrl: undefined,
  apiBody: undefined,
  status: QueryStatusEnum.New,
  data: [],
  lastRunBy: undefined,
  lastRunTs: 1,
  lastCancelTs: 1,
  lastCompleteTs: 1,
  lastCompleteDuration: undefined,
  lastErrorMessage: undefined,
  lastErrorTs: 1,
  queryJobId: undefined,
  bigqueryQueryJobId: undefined,
  bigqueryConsecutiveErrorsGetJob: undefined,
  bigqueryConsecutiveErrorsGetResults: undefined,
  serverTs: 1
};

let emptyTile: TileX = {
  modelId: undefined,
  modelLabel: undefined,
  modelFilePath: undefined,
  queryId: emptyQuery.queryId,
  mconfigId: emptyMconfig.mconfigId,
  trackChangeId: makeId(),
  listen: undefined,
  deletedFilterFieldIds: undefined,
  title: 'New Chart',
  plateWidth: undefined,
  plateHeight: undefined,
  plateX: undefined,
  plateY: undefined,
  mconfig: emptyMconfig,
  query: emptyQuery,
  hasAccessToModel: true
};

export const emptyChart: ChartState = {
  structId: undefined,
  chartId: EMPTY_CHART_ID,
  draft: false,
  creatorId: undefined,
  title: emptyTile.title,
  chartType: ChartTypeEnum.Table,
  modelId: undefined,
  modelLabel: undefined,
  filePath: undefined,
  space: undefined,
  accessRoles: [],
  accessRolesCombined: [],
  serverTs: undefined,
  tiles: [emptyTile],
  author: undefined,
  canEditOrDeleteChart: undefined
};

@Injectable({ providedIn: 'root' })
export class ChartQuery extends BaseQuery<ChartState> {
  constructor() {
    super(createStore({ name: 'chart' }, withProps<ChartState>(emptyChart)));
  }
}
